// @owners { team: patients }
import { ActionSheetContext } from '@alto/design-system';
import React, { useCallback, useContext } from 'react';
// eslint-disable-next-line import/no-deprecated
import { fetchPendingRateShipment } from '~shared/actions/rateShipment';
import { getShouldShowModal } from '~shared/features/rate-shipment/selectors/getShouldShowModal';
import { getShouldPromptUserForPhiAuth } from '~shared/features/users/selectors/getUserPhiAuth';
import { type ReduxStateShared, useDispatchShared, useSelectorShared, useStore } from '~shared/store';
import { RateShipmentActionSheet } from '~web/features/post-delivery/components/RateShipmentActionSheet';
import { PhiAuthActionSheet } from '~web/features/profile/components/PhiAuthActionSheet';

/**
 * Hook that runs on post auth and shows the appropriate action sheet based on the user's phi preferences
 * and whether a pending rate shipment exists.
 * @returns { showPhiAuth: boolean, showRateShipment: boolean } - Whether the PhiAuthActionSheet or RateShipmentActionSheet should be shown.
 */
export const useShowPostAuthActionSheets = () => {
  const dispatch = useDispatchShared();
  const store = useStore();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const showPhiAuth = useSelectorShared(getShouldPromptUserForPhiAuth);

  const handleShowPostAuthActionSheets = useCallback(async () => {
    if (showPhiAuth) {
      setActiveActionSheet(<PhiAuthActionSheet />);
      return { showPhiAuth: true, showRateShipment: false };
    } else {
      // eslint-disable-next-line import/no-deprecated
      const success = await dispatch(fetchPendingRateShipment());
      const shouldShowRateShipment = getShouldShowModal(store.getState() as ReduxStateShared);
      if (success && shouldShowRateShipment) {
        setActiveActionSheet(<RateShipmentActionSheet />);
        return { showPhiAuth: false, showRateShipment: true };
      }
    }
    return { showPhiAuth: false, showRateShipment: false };
  }, [dispatch, setActiveActionSheet, showPhiAuth, store]);

  return { handleShowPostAuthActionSheets };
};
